import { KycCheckStatus, KycSessionStatus, type Liveness } from '@/common/types';
import { Cardholder } from './Cardholder';
import {
  AmethystClient,
  type KycSessionAttachmentDto,
  type KycSessionDto,
  type KycSessionDtoHiddenSteps,
} from '@/integrations/Immersve/AmethystClient';
import { type StepName, UserFlow } from './UserFlow';
import { KycSessionStorage } from '@/common/KycSessionStorage';
import Countries from 'i18n-iso-countries';
import * as Sentry from '@sentry/vue';
import type {
  AttachKycDocumentProps,
  UploadFileProps,
  UploadFileResponseBody,
} from '@/integrations/Immersve/types';
import { KycSessionAttachment } from './KycSessionAttachment';
import { KycSessionFlow } from './KycSessionFlow';
import { config } from '@/config';
import { waitSeconds } from '@/util/waitUtil';
import {
  unsupportedDatabaseRegionCodes,
  poaRequiredWithDatabaseRegionCodes,
  RequiredStructuredAddressRegionCodes,
  AddressValidationAvailableRegionCodes,
  type RegionCode,
} from './Region';

export type KycSessionFeatureFlagKey = 'frankie-one-sdk-version' | 'frankie-onfido-sdk-version';
export type KycProvider = 'sumsub' | 'frankieOne';

export interface FeatureFlagKvp {
  key: KycSessionFeatureFlagKey;
  value?: string;
}

export interface KycSessionProps {
  redirectUrl: string;
  id: string;
  accountId: string;
  partner?: {
    name?: string;
    logoUrl?: string;
  };
  partnerAccountId: string;
  region?: string;
  externalId?: string;
  createdAt: string;
  liveness: Liveness;
  status: KycSessionStatus;
  kycCheck?: { status: KycCheckStatus };
  cardholder?: Cardholder;
  steps?: UserFlow;
  hiddenSteps?: StepName[];
  proofOfAddress?: KycSessionAttachment;
  kycProvider?: KycProvider; // Optional, if needed for provider-specific logic
  flags?: FeatureFlagKvp[];
  flow?: KycSessionFlow;
}

export function mapHiddenStepsToScreens(kycSessionDto: KycSessionDto): StepName[] | undefined {
  if (!kycSessionDto.hiddenSteps) {
    return undefined;
  }
  const hiddenScreens = kycSessionDto?.hiddenSteps.reduce(
    (screens: Set<StepName>, step: KycSessionDtoHiddenSteps) => {
      switch (step) {
        case 'region':
          // region must be set otherwise show the screen.
          if (kycSessionDto.region && Countries.isValid(kycSessionDto.region?.toUpperCase())) {
            screens.add('Region');
          } else {
            Sentry.captureMessage('region requested to be hidden. but not set');
          }
          return screens;
        case 'contact-channels':
          screens.add('Contact');
          return screens;
        case 'expected-spend':
          screens.add('Spending');
          return screens;
        case 'residential-address':
          screens.add('Address');
          screens.add('Region');
          return screens;
        default:
          return screens;
      }
    },
    new Set()
  );

  return Array.from(hiddenScreens);
}

export class KycSession {
  redirectUrl: string;
  id: string;
  accountId: string;
  partner?: {
    name?: string;
    logoUrl?: string;
  };
  partnerAccountId: string;
  region?: string;
  externalId?: string;
  createdAt: string;
  liveness: Liveness;
  status: KycSessionStatus;
  kycCheck?: { status: KycCheckStatus };
  cardholder: Cardholder;
  steps: UserFlow;
  hiddenSteps?: StepName[];
  proofOfAddress?: KycSessionAttachment;
  flags?: FeatureFlagKvp[];
  kycProvider?: KycProvider;
  flow?: KycSessionFlow;

  constructor(props: KycSessionProps) {
    this.redirectUrl = props.redirectUrl;
    this.id = props.id;
    this.accountId = props.accountId;
    this.partner = props.partner;
    this.partnerAccountId = props.partnerAccountId;
    this.region = props.region;
    this.externalId = props.externalId;
    this.createdAt = props.createdAt;
    this.liveness = props.liveness;
    this.status = props.status;
    this.kycCheck = props.kycCheck;
    this.cardholder = props.cardholder ?? Cardholder.forKycSession(this);
    this.hiddenSteps = props.hiddenSteps;
    this.steps = props.steps ?? UserFlow.forKycSession(this);
    this.proofOfAddress = props.proofOfAddress;
    this.kycProvider = props.kycProvider;
    this.flags = props.flags;
    this.flow = props.flow;
  }

  isKycRequired() {
    return this.kycCheck?.status !== KycCheckStatus.PASSED;
  }

  isKycPending() {
    return this.kycCheck?.status === KycCheckStatus.PENDING;
  }

  isPoaRequired() {
    const poaRegions = [...unsupportedDatabaseRegionCodes, ...poaRequiredWithDatabaseRegionCodes];
    return (
      this.isKycRequired() &&
      poaRegions.includes(this.region ?? '') &&
      this.kycProvider !== 'sumsub'
    );
  }

  async updateRegion(regionCode: string) {
    await new AmethystClient().updateKycSessionRegion(this.id, regionCode);
    KycSessionStorage.updateDataForSession(this.id, { region: regionCode });
    this.region = regionCode;
  }

  async uploadFile(uploadFileProps: UploadFileProps): Promise<UploadFileResponseBody> {
    return await new AmethystClient().uploadFile(uploadFileProps);
  }

  async getFileUpload(fileId: string): Promise<UploadFileResponseBody> {
    return await new AmethystClient().getFileUpload(fileId);
  }

  async attachKycSessionDocument(attachKycDocumentProps: AttachKycDocumentProps) {
    await new AmethystClient().attachKycSessionDocument(attachKycDocumentProps);
  }

  async refresh() {
    const sessionData = await KycSession.loadSessionData(this.id);

    if (!sessionData) {
      throw new Error('No session data found');
    }

    this.redirectUrl = sessionData.redirectUrl;
    this.status = sessionData.status;
    this.kycCheck = sessionData.kycCheck && { status: sessionData.kycCheck.status };
    this.kycProvider = sessionData.kycProvider;
    this.flow = sessionData.flow && KycSessionFlow.fromApiResponse(sessionData.flow);
  }

  async pushExternalProfile() {
    await new AmethystClient().pushExternalProfile(this.id);
    // pushExternalProfile triggers a kyc session job and returns immediately.
    // We wait here to give that job enough time to finish before starting
    // the IDV session, to avoid conflicts in FrankieOne.
    await waitSeconds(4);
  }

  async start() {
    return new AmethystClient().startKycSession(this.id);
  }

  async getExternalToken() {
    const response = await new AmethystClient().getExternalKycSessionToken(this.id);
    return response.token;
  }

  static findPoaAttachment(
    attachments: KycSessionAttachmentDto[] | undefined
  ): KycSessionAttachmentDto | undefined {
    const poaAttachment = attachments?.find((attachment) => attachment.type === 'proof-of-address');
    if (!poaAttachment) {
      return undefined;
    }
    return poaAttachment;
  }

  static fromApiResponse(kycSessionDto: KycSessionDto) {
    const { attachments, partnerName, partnerLogoUrl, flow, ...props } = kycSessionDto;
    const poaAttachment = KycSession.findPoaAttachment(attachments);
    const session = new KycSession({
      ...props,
      hiddenSteps: mapHiddenStepsToScreens(kycSessionDto),
      partner: {
        logoUrl: partnerLogoUrl,
        name: partnerName,
      },
      region: kycSessionDto.region?.toUpperCase(),
      proofOfAddress: poaAttachment
        ? KycSessionAttachment.createProofOfAddressAttachment(poaAttachment)
        : undefined,
      flow: flow && KycSessionFlow.fromApiResponse(flow),
    });

    return session;
  }

  static loadSessionData(id: string) {
    return new AmethystClient().getKycSessionData(id);
  }

  get oneSdkVersion() {
    if (config.customOneSdkVersionEnabled) {
      return config.oneSdkVersion;
    }
    const flagValue = this.flags?.find((flag) => flag.key === 'frankie-one-sdk-version')?.value;
    return flagValue ?? config.oneSdkVersion;
  }

  get onfidoSdkVersion() {
    if (config.customOnfidoSdkVersionEnabled) {
      return config.onfidoSdkVersion;
    }
    return (
      this.flags?.find((flag) => flag.key === 'frankie-onfido-sdk-version')?.value ??
      config.onfidoSdkVersion
    );
  }

  skipAutoPopulation() {
    return (
      this.partnerAccountId === 'bcbc0e3121ed19bfae2c486b828b1dd9' || // BGW live
      this.partnerAccountId === 'ad1b60dd974ab5ffcda95129548c7f6d' // BGW test
    );
  }

  isCompleted() {
    return this.status === KycSessionStatus.COMPLETED;
  }

  isCancelled() {
    return this.status === KycSessionStatus.CANCELLED;
  }

  isStructuredAddressRequired(country: RegionCode): boolean {
    return (
      RequiredStructuredAddressRegionCodes.includes(country) ||
      !AddressValidationAvailableRegionCodes.includes(country) ||
      this.kycProvider === 'sumsub'
    );
  }
}
